import { useDispatch } from "react-redux";
import { openModal, closeModal, openEditEquipmentModal, closeEditEquipmentModal } from "@store/slices/modalSlice";
import { addEquipment, updateEquipment } from "@store/slices/equipmentSlice";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { useCreateEquipment, useUpdateEquipment } from "../service/queries";
import type { Equipment } from "../types/types";
import type { CreateEquipmentRequest, UpdateEquipmentRequest } from "../types/types";
import { MODAL_KEYS } from "@constants/global.constants";
import { useTranslation } from "react-i18next";

export const useEquipmentModal = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	// Equipment mutations
	const createEquipmentMutation = useCreateEquipment();
	const updateEquipmentMutation = useUpdateEquipment();

	const openAddEquipmentModal = () => {
		dispatch(openModal(MODAL_KEYS?.ADD_EQUIPMENT));
	};

	const closeAddEquipmentModal = () => {
		dispatch(closeModal(MODAL_KEYS?.ADD_EQUIPMENT));
	};

	const openEditEquipmentModalHandler = (equipment: Equipment) => {
		dispatch(openEditEquipmentModal(equipment));
	};

	const closeEditEquipmentModalHandler = () => {
		dispatch(closeEditEquipmentModal());
	};

	// Business logic handlers
	const handleCreateEquipment = async (equipmentData: CreateEquipmentRequest) => {
		try {
			const response = await createEquipmentMutation.mutateAsync(equipmentData);
			const createdEquipment = response?.data || response;

			dispatch(addEquipment(createdEquipment));
			dispatch(closeModal(MODAL_KEYS?.ADD_EQUIPMENT));

			showNotification({
				message: t("feature.courseBuilder.equipments.success.createEquipment"),
				severity: SNACKBAR_SEVERITY?.SUCCESS,
			});
		} catch (error) {
			console.error(t("feature.courseBuilder.equipments.errors.failedToCreate"), error);
		}
	};

	const handleUpdateEquipment = async (equipmentData: UpdateEquipmentRequest, equipmentId: string) => {
		try {
			const response = await updateEquipmentMutation.mutateAsync({
				equipmentId,
				equipmentData,
			});

			const updatedEquipment = response?.data || response;
			dispatch(updateEquipment(updatedEquipment));
			dispatch(closeEditEquipmentModal());

			showNotification({
				message: t("feature.courseBuilder.equipments.success.updateEquipment"),
				severity: SNACKBAR_SEVERITY?.SUCCESS,
			});
		} catch (error) {
			console.error(t("feature.courseBuilder.equipments.errors.failedToUpdate"), error);
		}
	};

	return {
		openAddEquipmentModal,
		closeAddEquipmentModal,
		openEditEquipmentModal: openEditEquipmentModalHandler,
		closeEditEquipmentModal: closeEditEquipmentModalHandler,
		handleCreateEquipment,
		handleUpdateEquipment,
		isCreatingEquipment: createEquipmentMutation?.isPending,
		isUpdatingEquipment: updateEquipmentMutation?.isPending,
	};
};
